
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Box,
  Container,
  Badge,
  Menu,
  MenuItem,
  ClickAwayListener,
  Divider,
  InputBase,
  Paper,
  Collapse
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/firebase";
import { getData } from "../../Webservice/Webservice";
import { MdOutlineShoppingCart } from "react-icons/md";
import kanaaLogo from "../../assets/images/kanaalogo1.png";

const corfont = {
  fontFamily: "Cormorant, serif",
  fontStyle: "italic"
 };

const navItems = [
  { text: "Home", path: "/" },
  {text: "Services" },
  {text: "About Us", path: "/about" },
  {text: "Contact", path: "/contact" }
];

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchTimeoutRef = useRef(null);

  const [userInitial, setUserInitial] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  // Desktop profile menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const closeTimeout = useRef(null);

  // Drawer dropdown states
  const [drawerDropdownOpen, setDrawerDropdownOpen] = useState(false);
  const [accountInDrawerOpen, setAccountInDrawerOpen] = useState(false);

  const updateInitial = useCallback(() => {
    const storedName = localStorage.getItem("kanaaUserName") || "";
    setUserInitial(storedName.trim().charAt(0).toUpperCase());
  }, []);

  const fetchUserInfo = useCallback(async () => {
    const token = localStorage.getItem("kanaaToken");
    if (!token) {
      setUserInfo(null);
      setUserInitial("");
      return;
    }
    try {
      const response = await getData("customer", null, token, false);
      const profileData = response?.data || response?.customer || response?.result || response;
      if (profileData && typeof profileData === "object") {
        setUserInfo(profileData);
        const displayName =
          profileData?.name ||
          profileData?.fullName ||
          profileData?.customerName ||
          profileData?.userName ||
          profileData?.displayName ||
          "";
        if (displayName) {
          localStorage.setItem("kanaaUserName", displayName);
          setUserInitial(displayName.trim().charAt(0).toUpperCase());
        } else {
          updateInitial();
        }
      }
    } catch (err) {
      console.error("Failed to fetch user info:", err);
    }
  }, [updateInitial]);

  const fetchCartCount = useCallback(async () => {
    const token = localStorage.getItem("kanaaToken");
    if (!token) {
      setCartCount(0);
      return;
    }
    try {
      const response = await getData("customerCarts", null, token, false);
      const items = response?.data || [];
      setCartCount(items.length);
    } catch (err) {
      console.error("Failed to fetch cart count:", err);
      setCartCount(0);
    }
  }, []);

  useEffect(() => {
    updateInitial();
    fetchUserInfo();
    fetchCartCount();
    const onNameUpdated = () => updateInitial();
    const onCartUpdated = () => fetchCartCount();
    window.addEventListener("storage", onNameUpdated);
    window.addEventListener("kanaaUserNameUpdated", onNameUpdated);
    window.addEventListener("kanaaAuthUpdated", fetchUserInfo);
    window.addEventListener("kanaaCartUpdated", onCartUpdated);
    return () => {
      window.removeEventListener("storage", onNameUpdated);
      window.removeEventListener("kanaaUserNameUpdated", onNameUpdated);
      window.removeEventListener("kanaaAuthUpdated", fetchUserInfo);
      window.removeEventListener("kanaaCartUpdated", onCartUpdated);
    };
  }, [fetchUserInfo, fetchCartCount, updateInitial]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (value.trim().length >= 1) {
      searchTimeoutRef.current = setTimeout(() => {
        navigate(`/decor-cards?search=${encodeURIComponent(value.trim())}`);
        if (isMobile) setSearchOpen(false);
      }, 600);
    }
  };

  const toggleDrawer = (openState) => () => setDrawerOpen(openState);

  // Desktop hover handlers for profile menu
  const handleMouseEnter = (event) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    closeTimeout.current = setTimeout(() => setAnchorEl(null), 120);
  };

  const handleMenuMouseEnter = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  };

  const handleMenuMouseLeave = () => {
    closeTimeout.current = setTimeout(() => setAnchorEl(null), 120);
  };

  const handleCloseMenu = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setAnchorEl(null);
  };

  const handleServicesClick = () => {
    // Check if we're on the home page
    if (location.pathname === '/') {
      // Scroll to categories section with a small delay to ensure DOM is ready
      setTimeout(() => {
        const categoriesSection = document.getElementById('categories-section');
        if (categoriesSection) {
          categoriesSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          console.warn('Categories section not found');
        }
      }, 100);
    } else {
      // Navigate to home page and then scroll
      navigate('/');
      // Use multiple attempts to find the element after navigation
      let attempts = 0;
      const maxAttempts = 10;
      const scrollInterval = setInterval(() => {
        attempts++;
        const categoriesSection = document.getElementById('categories-section');
        if (categoriesSection) {
          categoriesSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
          clearInterval(scrollInterval);
        } else if (attempts >= maxAttempts) {
          clearInterval(scrollInterval);
          console.warn('Categories section not found after navigation');
        }
      }, 200);
    }
  };

  const handleLogout = async () => {
    handleCloseMenu();
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Firebase sign out failed:", error);
    } finally {
      localStorage.removeItem("kanaaToken");
      localStorage.removeItem("kanaaUserName");
      localStorage.removeItem("kanaaPhoneNumber");
      localStorage.removeItem("kanaaUserProfile");
      setUserInitial("");
      setUserInfo(null);
      setCartCount(0);
      window.dispatchEvent(new Event("kanaaUserNameUpdated"));
      window.dispatchEvent(new Event("kanaaAuthUpdated"));
      window.dispatchEvent(new Event("kanaaCartUpdated"));
      navigate("/login");
    }
  };

  // Drawer dropdown component
  function DrawerDropdown() {
    return (
      <Box>
        <ListItemButton onClick={() => setDrawerDropdownOpen((s) => !s)} sx={{ borderRadius: 1 }}>
          <ListItemText primary="More" sx={{ ...corfont }} />
          {drawerDropdownOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>

        <Collapse in={drawerDropdownOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton
              sx={{ pl: 4 }}
              component={RouterLink}
              to="/collections"
              onClick={toggleDrawer(false)}
            >
              <ListItemText primary="Collections" sx={{ ...corfont }} />
            </ListItemButton>
            <ListItemButton sx={{ pl: 4 }} component={RouterLink} to="/gifts" onClick={toggleDrawer(false)}>
              <ListItemText primary="Gifts" sx={{ ...corfont }} />
            </ListItemButton>
          </List>
        </Collapse>
      </Box>
    );
  }

  function AccountDropdownInDrawer({ onClose, userInitial }) {
    return (
      <Box>
        <ListItemButton onClick={() => setAccountInDrawerOpen((s) => !s)} sx={{ borderRadius: 1 }}>
          <ListItemText primary="Account" sx={{ ...corfont }} />
          {accountInDrawerOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>

        <Collapse in={accountInDrawerOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton component={RouterLink} to="/profile" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="Profile" sx={{ ...corfont }} />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/booking-orders" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="Booking Orders" sx={{ ...corfont }} />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/tickets" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="Raising Tickets" sx={{ ...corfont }} />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/wishlist" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="Wishlist" sx={{ ...corfont }} />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/policies" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="Privacy Policies" sx={{ ...corfont }} />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/terms" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="Terms & Conditions" sx={{ ...corfont }} />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/cancellation-policy" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="Cancellation Policy" sx={{ ...corfont }} />
            </ListItemButton>
            {userInitial ? (
              <MenuItem
                onClick={() => {
                  onClose();
                  handleLogout();
                }}
                sx={{ pl: 4 }}
              >
                <ListItemText primary="Logout" sx={{ ...corfont }} />
              </MenuItem>
            ) : (
              <ListItemButton component={RouterLink} to="/login" onClick={onClose} sx={{ pl: 4 }}>
                <ListItemText primary="Login" sx={{ ...corfont }} />
              </ListItemButton>
            )}
          </List>
        </Collapse>
      </Box>
    );
  }

  const DrawerContent = (
    <Box sx={{ width: 280, p: 2 }} role="presentation">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Box component="img" src={kanaaLogo} alt="logo" sx={{ height: 48 }} />
        <IconButton aria-label="close drawer" onClick={toggleDrawer(false)}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 1 }} />

      <List>
        {navItems.map((item) => {
          if (item.text === "Services") {
            return (
              <ListItemButton
                key={item.text}
                onClick={() => {
                  handleServicesClick();
                  toggleDrawer(false)();
                }}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText primary={item.text} sx={{ ...corfont }} />
              </ListItemButton>
            );
          }
          return (
            <ListItemButton
              key={item.text}
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={toggleDrawer(false)}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemText primary={item.text} sx={{ ...corfont }} />
            </ListItemButton>
          );
        })}
        
        <ListItemButton
          component={RouterLink}
          to="/cart"
          onClick={toggleDrawer(false)}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <ListItemText primary="Cart" sx={{ ...corfont }} />
            <Badge badgeContent={cartCount} color="error" sx={{ ml: 1 }}>
              <MdOutlineShoppingCart size={20} />
            </Badge>
          </Box>
        </ListItemButton>
      </List>

  

      

      <Divider sx={{ my: 1 }} />

      <AccountDropdownInDrawer onClose={toggleDrawer(false)} userInitial={userInitial} />
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "rgba(255,255,255,0.98)",
        backdropFilter: "blur(6px)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        py: { xs: 0.5, md: 1 }
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <Box component={RouterLink} to="/" sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <Box component="img" src={kanaaLogo} alt="Kanaa" sx={{ height: { xs: 36, md: 60 }, width: { xs: 110, md: 160 } }} />
            </Box>

            {!isMobile && (
              <Box sx={{ display: "flex", ml: 3, gap: 1.5 }}>
                {navItems.map((item) => {
                  if (item.text === "Services") {
                    return (
                      <Button
                        key={item.text}
                        onClick={handleServicesClick}
                        sx={{
                          fontSize: "20px",
                          color: "rgba(0, 0, 0, 0.87)",
                          fontWeight: 700,
                          position: "relative",
                          "&:hover": {
                            color: theme.palette.primary.main,
                            backgroundColor: "transparent"
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            width: "0",
                            height: "2px",
                            bottom: "4px",
                            left: 0,
                            backgroundColor: theme.palette.primary.main,
                            transition: "width 0.3s ease-in-out"
                          },
                          "&:hover::after": {
                            width: "100%"
                          },
                          ...corfont
                        }}
                      >
                        {item.text}
                      </Button>
                    );
                  }
                  return (
                    <Button
                      key={item.text}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        fontSize: "20px",
                        color: "rgba(0, 0, 0, 0.87)",
                        fontWeight: location.pathname === item.path ? 700 : 700,
                        position: "relative",
                        "&:hover": {
                          color: theme.palette.primary.main,
                          backgroundColor: "transparent"
                        },
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          width: location.pathname === item.path ? "100%" : "0",
                          height: "2px",
                          bottom: "4px",
                          left: 0,
                          backgroundColor: theme.palette.primary.main,
                          transition: "width 0.3s ease-in-out"
                        },
                        "&:hover::after": {
                          width: "100%"
                        },
                        ...corfont
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {!isMobile && (
              <Paper
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const t = searchValue.trim();
                  if (t) {
                    navigate(`/decor-cards?search=${encodeURIComponent(t)}`);
                    setSearchValue("");
                  }
                }}
                sx={{ display: "flex", alignItems: "center", px: 1, mr: 1, minWidth: 260 }}
              >
                <InputBase
                  placeholder="Search decors..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  inputProps={{ "aria-label": "search decors" }}
                  sx={{ ml: 1, flex: 1, fontFamily: corfont.fontFamily }}
                />
                <IconButton type="submit" aria-label="search">
                  <SearchIcon />
                </IconButton>
              </Paper>
            )}

            {isMobile && (
              <IconButton aria-label="open search" onClick={() => setSearchOpen((s) => !s)} sx={{ mr: 0.5 }}>
                <SearchIcon />
              </IconButton>
            )}

            <IconButton component={RouterLink} to="/cart" aria-label="cart">
              <Badge badgeContent={cartCount} color="error">
                <MdOutlineShoppingCart size={22} />
              </Badge>
            </IconButton>

            <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
              <IconButton aria-controls={open ? "account-menu" : undefined} aria-haspopup="true" aria-expanded={open ? "true" : undefined} onClick={() => {}}>
                {userInitial ? (
                  <Box sx={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: theme.palette.primary.main, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600,...corfont }}>
                    {userInitial}
                  </Box>
                ) : (
                  <AccountCircleIcon />
                )}
              </IconButton>

              <Menu
                id="account-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseMenu}
                MenuListProps={{ onMouseEnter: handleMenuMouseEnter, onMouseLeave: handleMenuMouseLeave }}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <ClickAwayListener onClickAway={handleCloseMenu}>
                 <Box>
  {userInitial ? (
    <>
      <MenuItem
        component={RouterLink}
        to="/profile"
        onClick={handleCloseMenu}
        sx={{ ...corfont, fontWeight: 600, fontSize: "20px" }}
      >
        Profile
      </MenuItem>

      <MenuItem
        component={RouterLink}
        to="/booking-orders"
        onClick={handleCloseMenu}
        sx={{ ...corfont, fontWeight: 600, fontSize: "20px" }}
      >
        Booking Orders
      </MenuItem>

      <MenuItem
        component={RouterLink}
        to="/tickets"
        onClick={handleCloseMenu}
        sx={{ ...corfont, fontWeight: 600, fontSize: "20px" }}
      >
        Raising Tickets
      </MenuItem>

      <MenuItem
        component={RouterLink}
        to="/terms"
        onClick={handleCloseMenu}
        sx={{ ...corfont, fontWeight: 600, fontSize: "20px" }}
      >
        Terms & Conditions
         </MenuItem>
        <MenuItem
        component={RouterLink}
        to="/policies"
        onClick={handleCloseMenu}
        sx={{ ...corfont, fontWeight: 600,fontSize: "20px"  }}
      >
        Policies
       </MenuItem>
                        
       <MenuItem
        component={RouterLink}
        to="/cancellation-policy"
        onClick={handleCloseMenu}
        sx={{ ...corfont, fontWeight: 600 , fontSize: "20px"}}
      >
        Cancellation Policy
       </MenuItem>
                        
       <MenuItem
        component={RouterLink}
        to="/wishlist"
        onClick={handleCloseMenu}
        sx={{ ...corfont, fontWeight: 600, fontSize: "20px" }}
      >
        Wishlist
      </MenuItem>

      <Divider />

      <MenuItem
        onClick={handleLogout}
        sx={{ ...corfont, fontWeight: 600, fontSize: "20px" }}
      >
        Logout
      </MenuItem>
    </>
  ) : (
    <MenuItem
      component={RouterLink}
      to="/login"
      onClick={handleCloseMenu}
      sx={{ ...corfont, fontWeight: 600 , fontSize: "20px" }}
    >
      Login
    </MenuItem>
  )}
</Box>

                </ClickAwayListener>
              </Menu>
            </Box>

            {isMobile && (
              <IconButton aria-label="open menu" onClick={toggleDrawer(true)}>
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>

      {isMobile && searchOpen && (
        <Box sx={{ position: "fixed", left: 0, right: 0, top: 64, zIndex: 1400, px: 2 }}>
          <Paper
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              const t = searchValue.trim();
              if (t) {
                navigate(`/decor-cards?search=${encodeURIComponent(t)}`);
                setSearchOpen(false);
                setSearchValue("");
              }
            }}
            sx={{ display: "flex", gap: 1, alignItems: "center", p: 1 }}
          >
            <InputBase autoFocus placeholder="Search decors (type to search)..." value={searchValue} onChange={handleSearchChange} sx={{ ml: 1, flex: 1 }} />
            <IconButton type="submit">
              <SearchIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setSearchOpen(false);
                setSearchValue("");
              }}
            >
              <CloseIcon />
            </IconButton>
          </Paper>
        </Box>
      )}

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        {DrawerContent}
      </Drawer>
    </AppBar>
  );
}
